import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Input, Button, Card, Divider, message, Spin } from "antd";
import {
  UserOutlined,
  LockOutlined,
  GithubOutlined,
  QqOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import axios from "axios";
import logo from "../assets/images/hongmoai-logo-black.webp";
import "../styles/Login.css";
import { useGlobal } from "../contexts/GlobalContext";
import request from "../utils/request";
import { checkToken, persistToken } from "../utils/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n";

interface LoginForm {
  email: string;
  password: string;
}

interface RegisterForm {
  email: string;
  verificationCode: string;
  password: string;
  confirmPassword: string;
  captcha: string;
}

interface ResetPasswordForm {
  email: string;
  verificationCode: string;
  newPassword: string;
  confirmPassword: string;
}

interface CaptchaResponse {
  code: number;
  msg: string;
  data: {
    captcha_id: string;
    captcha_img: string;
  };
}

// 添加接口响应类型定义
interface LoginResponse {
  code: number;
  msg: string;
  data?: {
    token: string;
  };
}

// 在文件顶部添加WebAuthn相关类型
interface PasskeyLoginBeginResponse {
  publicKey: {
    challenge: string;
    allowCredentials: Array<{
      id: string;
      type: string;
    }>;
    rpId: string;
    timeout: number;
    userVerification: string;
  };
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [form] = Form.useForm();
  const [countdown, setCountdown] = useState(0);
  const { API_BASE_URL, setToken, token } = useGlobal();
  const [captchaId, setCaptchaId] = useState("");
  const [captchaImg, setCaptchaImg] = useState("");
  const [autoLoginLoading, setAutoLoginLoading] = useState(false);
  // 添加一个标志位，用于标记是否已经显示了登录成功消息
  const hasShownSuccessMessage = useRef(false);
  // 添加登录按钮加载状态
  const [loginButtonLoading, setLoginButtonLoading] = useState(false);
  // 获取是否有重定向URL
  const hasRedirectURL = useRef(false);
  // 添加状态以检测是否在iframe中运行
  const [isInIframe, setIsInIframe] = useState(false);

  // passkey相关状态
  const [isUsingPasskey, setIsUsingPasskey] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [rememberedEmail, setRememberedEmail] = useState<string>("");

  // 获取URL参数
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tokenFromUrl = queryParams.get("token");
  const codeFromUrl = queryParams.get("code");
  const inviteIdFromUrl = queryParams.get("InviteID");

  const navigate = useNavigate();

  // 检查是否有重定向URL
  useEffect(() => {
    const redirectURL = sessionStorage.getItem("redirectAfterLogin");
    hasRedirectURL.current = !!redirectURL;
  }, []);

  // 检测页面是否在iframe中运行
  useEffect(() => {
    try {
      setIsInIframe(window.self !== window.top);
    } catch (e) {
      // 如果无法访问window.top (可能因为跨域限制)，那么页面肯定在iframe中
      setIsInIframe(true);
    }
  }, []);

  useEffect(() => {
    // 如果URL中包含邀请码，保存到localStorage
    if (inviteIdFromUrl) {
      localStorage.setItem("inviteId", inviteIdFromUrl);
    }
  }, [inviteIdFromUrl]);

  const handlePasskeyLogin = async (email: string) => {
    if (!email) {
      message.error(t("auth.msg.enterEmailFirst"));
      return;
    }

    try {
      setPasskeyLoading(true);

      localStorage.setItem("rememberedEmail", email);

      const beginResponse = await request.post<{
        code: number;
        msg: string;
        data: PasskeyLoginBeginResponse;
      }>(`${API_BASE_URL}/api/v3/auth/passkey/login/begin?email=${encodeURIComponent(email)}`);

      if (beginResponse.data.code !== 200) {
        message.error(beginResponse.data.msg || t("auth.msg.passkeyFailed"));
        return;
      }

      // 准备WebAuthn选项
      const publicKey = beginResponse.data.data.publicKey;
      const publicKeyCredentialRequestOptions = {
        challenge: base64UrlDecode(publicKey.challenge),
        allowCredentials: publicKey.allowCredentials.map((cred) => ({
          id: base64UrlDecode(cred.id),
          type: cred.type,
        })),
        timeout: publicKey.timeout,
        rpId: publicKey.rpId,
        userVerification: publicKey.userVerification,
      };

      // 调用浏览器的Web Authentication API
      const credential = (await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions as any,
      })) as PublicKeyCredential;

      // 准备发送到服务器的数据
      const authData = {
        id: credential.id,
        rawId: base64UrlEncode(new Uint8Array(credential.rawId)),
        type: credential.type,
        response: {
          authenticatorData: base64UrlEncode(
            new Uint8Array(
              (
                credential.response as AuthenticatorAssertionResponse
              ).authenticatorData
            )
          ),
          clientDataJSON: base64UrlEncode(
            new Uint8Array(
              (
                credential.response as AuthenticatorAssertionResponse
              ).clientDataJSON
            )
          ),
          signature: base64UrlEncode(
            new Uint8Array(
              (credential.response as AuthenticatorAssertionResponse).signature
            )
          ),
          userHandle: (credential.response as AuthenticatorAssertionResponse)
            .userHandle
            ? base64UrlEncode(
                new Uint8Array(
                  (credential.response as AuthenticatorAssertionResponse)
                    .userHandle as ArrayBuffer
                )
              )
            : null,
        },
      };

      // 完成Passkey登录
      const finishResponse = await request.post<{
        code: number;
        msg: string;
        data: { token: string };
      }>(
        `${API_BASE_URL}/api/v3/auth/passkey/login/finish?email=${encodeURIComponent(email)}`,
        authData
      );

      if (finishResponse.data.code !== 200) {
        message.error(finishResponse.data.msg || t("auth.msg.passkeyVerifyFailed"));
        return;
      }

      hasShownSuccessMessage.current = true;
      persistToken(finishResponse.data.data.token, setToken);

      message.success(t("auth.msg.loginSuccessConsole"));

      // 检查是否有保存的重定向URL
      const redirectURL = sessionStorage.getItem("redirectAfterLogin");
      if (redirectURL) {
        sessionStorage.removeItem("redirectAfterLogin");
        if (redirectURL.startsWith("http") || redirectURL.startsWith("//")) {
          window.location.href = redirectURL;
        } else {
          handleNavigation(redirectURL);
        }
      } else {
        handleNavigation("/console");
      }
    } catch (error: any) {
      if (error.name === "NotAllowedError") {
        message.error(t("auth.msg.opCancelled"));
      } else if (error.name === "SecurityError") {
        message.error(t("auth.msg.securityError"));
      } else if (error.name === "AbortError") {
        message.error(t("auth.msg.opAborted"));
      } else if (error.response && error.response.data) {
        message.error(
          error.response.data.msg || t("auth.msg.passkeyTryPassword")
        );
      } else {
        message.error(error.message || t("auth.msg.passkeyTryPassword"));
      }
      console.error("通行密钥登录错误:", error);
    } finally {
      setPasskeyLoading(false);
    }
  };

  const base64UrlDecode = (base64Url: string): ArrayBuffer => {
    const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");

    const rawData = window.atob(base64);
    const buffer = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; i++) {
      buffer[i] = rawData.charCodeAt(i);
    }

    return buffer.buffer;
  };

  const base64UrlEncode = (arrayBuffer: Uint8Array): string => {
    const base64 = window.btoa(
      Array.from(new Uint8Array(arrayBuffer))
        .map((byte) => String.fromCharCode(byte))
        .join("")
    );
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
  };

  // 检测浏览器是否支持Passkey
  const isPasskeySupported =
    typeof window !== "undefined" &&
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === "function";

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setRememberedEmail(savedEmail);
      form.setFieldsValue({ email: savedEmail });

      if (isPasskeySupported) {
        setIsUsingPasskey(true);
      }
    }
  }, [form, isPasskeySupported]);
  // 验证 OAuth Code
  const verifyOAuthCode = async (
    token: string,
    code: string
  ): Promise<boolean> => {
    try {
      // 这里应该调用后端API验证code是否有效
      // 由于目前没有这个API，我们先假设code有效
      console.log("验证OAuth Code:", code);
      return true;
    } catch (error) {
      console.error("验证OAuth Code失败:", error);
      return false;
    }
  };

  // 处理通过链接自动登录
  useEffect(() => {
    const handleAutoLogin = async () => {
      if (tokenFromUrl && codeFromUrl) {
        console.log("检测到登录参数:", {
          token: tokenFromUrl,
          code: codeFromUrl,
        });
        setAutoLoginLoading(true);

        try {
          // 验证token是否有效
          const isTokenValid = checkToken(tokenFromUrl);
          console.log("Token验证结果:", isTokenValid);

          if (isTokenValid) {
            // 验证OAuth Code
            const isCodeValid = await verifyOAuthCode(
              tokenFromUrl,
              codeFromUrl
            );
            console.log("OAuth Code验证结果:", isCodeValid);

            if (isCodeValid) {
              // 设置标志位，表示已经显示了登录成功消息
              hasShownSuccessMessage.current = true;

              // 设置token到全局状态
              persistToken(tokenFromUrl, setToken);
              console.log("Token已设置到全局状态");

              // 清除URL中的参数
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              );
              console.log("URL参数已清除");

              // 检查是否有保存的重定向URL
              const redirectURL = sessionStorage.getItem("redirectAfterLogin");
              // 延迟跳转，确保token已被正确设置
              setTimeout(() => {
                console.log("准备跳转到页面");
                if (redirectURL) {
                  // 清除保存的URL
                  sessionStorage.removeItem("redirectAfterLogin");
                  // redirectAfterLogin 存的是含 /console 前缀的完整路径,
                  // BrowserRouter basename=/console 下 navigate 会双拼前缀(U16),统一用 location 跳转
                  window.location.href = redirectURL;
                } else {
                  // 没有保存的URL,跳转到控制台首页(basename 内根路径)
                  navigate("/");
                }
              }, 1500);
            } else {
              console.error("OAuth Code无效");
              message.error(t("auth.msg.linkInvalid"));
              setAutoLoginLoading(false);
            }
          } else {
            console.error("Token无效");
            message.error(t("auth.msg.linkExpired"));
            setAutoLoginLoading(false);
          }
        } catch (error) {
          console.error("自动登录过程中发生错误:", error);
          message.error(t("auth.msg.loginFailedManual"));
          setAutoLoginLoading(false);
        }
      } else {
        console.log("未检测到登录参数");
      }
    };

    handleAutoLogin();
  }, [tokenFromUrl, codeFromUrl, setToken, navigate, t]);

  // 检查用户是否已登录，如果已登录则先处理邀请码，再跳转到 console 首页
  useEffect(() => {
    const handleInviteAndRedirect = async () => {
      if (
        token &&
        checkToken(token) &&
        !autoLoginLoading &&
        !hasShownSuccessMessage.current
      ) {
        // 如果有邀请码，先处理邀请
        const inviteId = localStorage.getItem("inviteId");
        if (inviteId) {
          try {
            const response = await request.post(
              `${API_BASE_URL}/api/v3/invite/bind`,
              {
                code: inviteId,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (response.data.code === 200 || response.data.code === 400) {
              // 成功后删除localStorage中的邀请码
              localStorage.removeItem("inviteId");
            }
          } catch (error: any) {
            if (error.response?.data?.code === 400) {
              localStorage.removeItem("inviteId");
            }
            console.error("检查邀请码失败:", error);
          }
        }

        // 设置标志位，表示已经显示了登录成功消息
        hasShownSuccessMessage.current = true;

        // 检查是否有保存的重定向URL
        const redirectURL = sessionStorage.getItem("redirectAfterLogin");
        if (redirectURL) {
          // 清除保存的URL
          sessionStorage.removeItem("redirectAfterLogin");
          // 重定向到保存的URL
          window.location.href = redirectURL;
        } else {
          // 没有保存的URL，跳转到控制台首页
          window.location.href = "/console";
        }
      }
    };

    handleInviteAndRedirect();
  }, [token, autoLoginLoading, API_BASE_URL]);

  // 预加载资源的函数
  const preloadResources = useCallback(() => {
    // 预加载控制台页面可能需要的图标和样式
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = "/console";
    document.head.appendChild(link);
  }, []);

  // 处理导航的函数
  const handleNavigation = useCallback(
    (path: string) => {
      // 先预加载资源
      preloadResources();

      // 使用 setTimeout 确保预加载有时间完成
      setTimeout(() => {
        navigate(path);
      }, 100);
    },
    [navigate, preloadResources]
  );

  // 修改检查登录状态的 useEffect
  useEffect(() => {
    if (token && checkToken(token) && !autoLoginLoading) {
      // 检查是否有保存的重定向URL
      const redirectURL = sessionStorage.getItem("redirectAfterLogin");
      if (redirectURL) {
        // 清除保存的URL
        sessionStorage.removeItem("redirectAfterLogin");
        // 如果是外部URL，使用window.location.href
        if (redirectURL.startsWith("http") || redirectURL.startsWith("//")) {
          window.location.href = redirectURL;
        } else {
          // 内部路由使用navigate
          handleNavigation(redirectURL);
        }
      } else {
        // 导航到控制台首页
        handleNavigation("/console");
      }
    }
  }, [token, autoLoginLoading, navigate, handleNavigation]);

  const onFinish = async (values: LoginForm) => {
    setLoginButtonLoading(true);
    try {
      const response = await request.post<LoginResponse>(
        `${API_BASE_URL}/api/v3/auth/login`,
        {
          email: values.email,
          password: values.password,
        }
      );

      if (response.data.code === 200 && response.data.data?.token) {
        // 设置标志位，表示已经显示了登录成功消息
        hasShownSuccessMessage.current = true;
        persistToken(response.data.data.token, setToken);

        localStorage.setItem("rememberedEmail", values.email);

        // 检查是否有保存的重定向URL
        const redirectURL = sessionStorage.getItem("redirectAfterLogin");
        if (redirectURL) {
          // 清除保存的URL
          sessionStorage.removeItem("redirectAfterLogin");
          // 登录成功消息
          message.success(t("auth.msg.loginSuccessBack"));
          // 重定向到保存的URL
          if (redirectURL.startsWith("http") || redirectURL.startsWith("//")) {
            window.location.href = redirectURL;
          } else {
            handleNavigation(redirectURL);
          }
        } else {
          // 没有保存的URL，跳转到控制台首页
          message.success(t("auth.msg.loginSuccessConsole"));
          handleNavigation("/console");
        }
      } else {
        setLoginButtonLoading(false);
        message.error(response.data.msg || t("auth.msg.loginFailed"));
      }
    } catch (error: any) {
      setLoginButtonLoading(false);
      if (axios.isAxiosError(error)) {
        const response = error.response?.data as LoginResponse;

        // 处理不同的错误情况
        if (response?.msg.includes("尝试次数过多")) {
          message.error(t("auth.msg.tooManyAttempts"));
        } else if (response?.msg.includes("用户名或密码错误")) {
          message.error(t("auth.msg.wrongCredentials"));
        } else if (response?.msg.includes("参数错误")) {
          message.error(t("auth.msg.enterValidEmailPwd"));
        } else {
          message.error(response?.msg || t("auth.msg.loginFailedRetry"));
        }
      } else {
        message.error(t("auth.msg.systemError"));
      }
    }
  };

  const handleGithubLogin = () => {
    const clientId = "Ov23liGj9NmbjEwJMfPH";
    const redirectUri = encodeURIComponent(
      "https://ai.zhheo.com/console/login/github"
    );
    const scope = "user:email";
    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;

    window.location.href = githubAuthUrl;
  };

  const handleQQLogin = () => {
    const clientId = "102689824";
    const redirectUri = encodeURIComponent(
      "https://ai.zhheo.com/console/login/qq"
    );
    const scope = "get_unionid";
    const qqAuthUrl = `https://graph.qq.com/oauth2.0/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;

    window.location.href = qqAuthUrl;
  };

  const getCaptcha = useCallback(async () => {
    try {
      const response = await request.get<CaptchaResponse>(
        `${API_BASE_URL}/api/v3/auth/captcha`
      );
      if (response.data.code === 200) {
        setCaptchaId(response.data.data.captcha_id);
        setCaptchaImg(response.data.data.captcha_img);
      } else {
        message.error(t("auth.msg.captchaFailed"));
      }
    } catch (error) {
      message.error(t("auth.msg.captchaFailed"));
    }
  }, [API_BASE_URL, t]);

  useEffect(() => {
    if (!isLogin) {
      getCaptcha();
    }
  }, [isLogin, getCaptcha]);

  const onRegisterFinish = async (values: RegisterForm) => {
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/auth/register`,
        {
          email: values.email,
          password: values.password,
          verify_code: values.verificationCode,
          captcha: values.captcha,
          captcha_id: captchaId,
        }
      );

      if (response.data.code === 200) {
        message.success(t("auth.msg.registerSuccess"));
        persistToken(response.data.data.token, setToken);
        handleNavigation("/console");
      } else {
        message.error(response.data.msg || t("auth.msg.registerFailed"));
        getCaptcha();
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || t("auth.msg.registerFailed"));
      getCaptcha();
    }
  };

  const handleSendVerificationCode = async () => {
    try {
      // 先触发邮箱字段的验证
      try {
        await form.validateFields(["email"]);
      } catch (err) {
        return;
      }

      const email = form.getFieldValue("email");

      if (!email) {
        message.error(t("auth.msg.enterEmailFirst"));
        return;
      }

      // 立即开始倒计时
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // 根据不同场景使用不同的API
      const response = isResetting
        ? await request.post(`${API_BASE_URL}/api/v3/auth/password/reset-code`, {
            email,
          })
        : await request.post(`${API_BASE_URL}/api/v3/auth/verify-code`, {
            email,
          });

      if (response.data.code === 200) {
        message.success(t("auth.msg.codeSent"));
      } else {
        message.error(response.data.msg || t("auth.msg.sendCodeFailed"));
        // 如果发送失败，清除倒计时
        clearInterval(timer);
        setCountdown(0);
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || t("auth.msg.sendCodeFailed"));
      // 如果发送失败，清除倒计时
      setCountdown(0);
    }
  };

  const onResetPasswordFinish = async (values: ResetPasswordForm) => {
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/auth/password/reset`,
        {
          email: values.email,
          password: values.newPassword,
          code: values.verificationCode,
        }
      );

      if (response.data.code === 200) {
        message.success(t("auth.msg.resetSuccess"));
        setIsResetting(false);
        setIsLogin(true);
      } else {
        message.error(response.data.msg || t("auth.msg.resetFailed"));
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || t("auth.msg.resetFailedRetry"));
    }
  };

  // 修复正则表达式中的转义字符
  const passwordPattern =
    /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!@#$%^&*()_+\-=[\]{};:'",.<>/?]{8,}$/;

  const loginForm = (
    <Form
      form={form}
      name="login"
      onFinish={onFinish}
      autoComplete="off"
      size="large"
      initialValues={{ email: rememberedEmail }}
    >
      <Form.Item
        name="email"
        rules={[{ required: true, message: "请输入邮箱或旧用户名" }]}
      >
        <Input prefix={<UserOutlined />} placeholder="邮箱或旧用户名" />
      </Form.Item>

      {!isUsingPasskey && (
        <Form.Item
          name="password"
          rules={[{ required: true, message: t("auth.rules.requiredPassword") }]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder={t("auth.password")} />
        </Form.Item>
      )}

      <Form.Item style={{ marginBottom: 8 }}>
        {isUsingPasskey ? (
          <Button
            type="primary"
            block
            loading={passkeyLoading}
            onClick={() => handlePasskeyLogin(form.getFieldValue("email"))}
            icon={<i className="ri-key-fill" style={{ marginRight: 8 }} />}
          >
            {t("auth.passkeyLogin")}
          </Button>
        ) : (
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={loginButtonLoading}
          >
            {t("auth.passwordLogin")}
          </Button>
        )}
      </Form.Item>

      {isPasskeySupported && (
        <Form.Item>
          <Button block onClick={() => setIsUsingPasskey(!isUsingPasskey)}>
            {isUsingPasskey ? (
              t("auth.usePassword")
            ) : (
              <>
                <i className="ri-key-fill" style={{ marginRight: 4 }} />
                {t("auth.usePasskey")}
              </>
            )}
          </Button>
        </Form.Item>
      )}

      <Form.Item>
        <div
          className="login-actions"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "space-between",
          }}
        >
          <Button type="link" onClick={() => setIsLogin(false)}>
            {t("auth.noAccount")}
          </Button>
          <Button type="link" onClick={() => setIsResetting(true)}>
            {t("auth.forgotPassword")}
          </Button>
        </div>
      </Form.Item>
      {hasRedirectURL.current && (
        <p style={{ textAlign: "center", fontSize: "13px", color: "#888" }}>
          {t("auth.autoReturnTip")}
        </p>
      )}
    </Form>
  );

  const registerForm = (
    <Form
      form={form}
      name="register"
      onFinish={onRegisterFinish}
      autoComplete="off"
      size="large"
    >
      <Form.Item
        name="email"
        rules={[
          { required: true, message: t("auth.rules.requiredEmail") },
          { type: "email", message: t("auth.rules.invalidEmail") },
        ]}
      >
        <Input prefix={<UserOutlined />} placeholder={t("auth.email")} />
      </Form.Item>

      <Form.Item
        name="verificationCode"
        rules={[{ required: true, message: t("auth.rules.requiredEmailCode") }]}
      >
        <Input
          prefix={<SafetyOutlined />}
          placeholder={t("auth.emailVerifyCode")}
          addonAfter={
            <Button
              type="link"
              size="small"
              onClick={handleSendVerificationCode}
              disabled={countdown > 0}
            >
              {countdown > 0 ? t("auth.retryAfter", { n: countdown }) : t("auth.sendCode")}
            </Button>
          }
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          { required: true, message: t("auth.rules.requiredPassword") },
          { min: 8, message: t("auth.rules.passwordMin8") },
          {
            pattern: passwordPattern,
            message: t("auth.rules.passwordPattern"),
          },
        ]}
      >
        <Input.Password prefix={<LockOutlined />} placeholder={t("auth.password")} />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        dependencies={["password"]}
        rules={[
          { required: true, message: t("auth.rules.requiredConfirm") },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t("auth.rules.passwordMismatch")));
            },
          }),
        ]}
      >
        <Input.Password prefix={<LockOutlined />} placeholder={t("auth.confirmPassword")} />
      </Form.Item>

      <Form.Item
        name="captcha"
        rules={[{ required: true, message: t("auth.rules.requiredCaptcha") }]}
      >
        <Input
          prefix={<SafetyOutlined />}
          placeholder={t("auth.imageCaptcha")}
          addonAfter={
            <div onClick={getCaptcha} style={{ cursor: "pointer" }}>
              {captchaImg ? (
                <img src={captchaImg} alt={t("auth.captchaAlt")} style={{ height: "32px" }} />
              ) : (
                t("auth.getCaptcha")
              )}
            </div>
          }
        />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" block>
          {t("auth.register")}
        </Button>
      </Form.Item>
      <Form.Item>
        <Button type="link" block onClick={() => setIsLogin(true)}>
          {t("auth.haveAccount")}
        </Button>
      </Form.Item>
    </Form>
  );

  const resetPasswordForm = (
    <Form
      form={form}
      name="resetPassword"
      onFinish={onResetPasswordFinish}
      autoComplete="off"
      size="large"
    >
      <Form.Item
        name="email"
        rules={[
          { required: true, message: t("auth.rules.requiredEmail") },
          { type: "email", message: t("auth.rules.invalidEmail") },
        ]}
      >
        <Input prefix={<UserOutlined />} placeholder={t("auth.email")} />
      </Form.Item>

      <Form.Item
        name="verificationCode"
        rules={[{ required: true, message: t("auth.rules.requiredVerifyCode") }]}
      >
        <Input
          prefix={<SafetyOutlined />}
          placeholder={t("auth.verifyCode")}
          addonAfter={
            <Button
              type="link"
              size="small"
              onClick={handleSendVerificationCode}
              disabled={countdown > 0}
            >
              {countdown > 0 ? t("auth.retryAfter", { n: countdown }) : t("auth.sendCode")}
            </Button>
          }
        />
      </Form.Item>

      <Form.Item
        name="newPassword"
        rules={[
          { required: true, message: t("auth.rules.requiredNewPassword") },
          { min: 6, message: t("auth.rules.passwordMin6") },
        ]}
      >
        <Input.Password prefix={<LockOutlined />} placeholder={t("auth.newPassword")} />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        dependencies={["newPassword"]}
        rules={[
          { required: true, message: t("auth.rules.requiredConfirmNew") },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("newPassword") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t("auth.rules.passwordMismatch")));
            },
          }),
        ]}
      >
        <Input.Password prefix={<LockOutlined />} placeholder={t("auth.confirmNewPassword")} />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" block>
          {t("auth.resetPassword")}
        </Button>
      </Form.Item>
      <Form.Item>
        <Button
          type="link"
          block
          onClick={() => {
            setIsResetting(false);
            setIsLogin(true);
          }}
        >
          {t("auth.backToLogin")}
        </Button>
      </Form.Item>
    </Form>
  );

  return (
    <div
      className="login-container"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 16px",
      }}
    >
      <div
        style={{
          flex: "1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          flexDirection: "column",
        }}
      >
        {autoLoginLoading ? (
          <Card
            className="login-card"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Spin size="large" tip={t("auth.autoLoggingIn")} />
          </Card>
        ) : (
          <Card className="login-card">
            <div className="login-header">
              <img src={logo} alt="洪墨AI" className="login-logo" />

              <h2 className="login-subtitle">
                {isResetting ? t("auth.resetTitle") : isLogin ? "" : t("auth.registerTitle")}
              </h2>
              {!isLogin && !isResetting && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "13px",
                    color: "#888",
                    marginTop: "8px",
                  }}
                >
                  <i
                    className="ri-information-line"
                    style={{ marginRight: "4px", color: "#1890ff" }}
                  ></i>
                  {t("auth.legacyTip")}
                </div>
              )}
            </div>
            {isResetting
              ? resetPasswordForm
              : isLogin
              ? loginForm
              : registerForm}
            {isLogin && !isResetting && !isInIframe && (
              <>
                <Divider>{t("auth.otherLogin")}</Divider>
                <div
                  className="social-login"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    icon={<GithubOutlined />}
                    onClick={handleGithubLogin}
                    size="large"
                  >
                    {t("auth.githubLogin")}
                  </Button>
                  <Button
                    icon={<QqOutlined />}
                    onClick={handleQQLogin}
                    size="large"
                    type="primary"
                  >
                    {t("auth.qqLogin")}
                  </Button>
                </div>
              </>
            )}
          </Card>
        )}
      </div>
      <div
        style={{
          width: "100%",
          textAlign: "center",
          marginTop: "24px",
          color: "#666",
          fontSize: "14px",
        }}
      >
        <div style={{ marginBottom: "12px" }}>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#666", textDecoration: "none" }}
          >
            京ICP备19051325号-2
          </a>
          {" | "}
          <a
            href="http://www.beian.gov.cn/portal/registerSystemInfo"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#666", textDecoration: "none" }}
          >
            京公网安备11011402054421号
          </a>
        </div>
        <div style={{ marginBottom: "12px" }}>
          {t("auth.operatedBy")}
        </div>
        <div>
          <a
            href="https://postchat.zhheo.com/infouser.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#666", marginRight: "16px" }}
          >
            {t("auth.userAgreement")}
          </a>
          <a
            href="https://postchat.zhheo.com/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#666" }}
          >
            {t("auth.privacy")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
